/**
 * HealthLogsPage - Main health logs page with child selection
 * BP-03-04: Health Frontend
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  Plus,
  Search,
  Filter,
  Calendar,
  RefreshCw,
  ChevronDown,
  X,
  FileText,
  Pill,
  Moon,
  Zap,
  Heart,
  Thermometer,
  UtensilsCrossed,
  Smile,
  Clock,
  TrendingUp,
  AlertCircle,
  User,
} from 'lucide-react';
import { useFamilyContext } from '@/features/family/hooks/useFamilyContext';
import { NoFamilyState } from '@/features/family/components/NoFamilyState';
import { useChildren } from '@/features/children/hooks/useChildren';
import { useHealthLogs, useDeleteHealthLog } from '../hooks/useHealthLogs';
import { useHealthLogStats } from '../hooks/useHealthLogStats';
import { QUICK_ADD_OPTIONS, type HealthLog, type HealthLogTypeSlug } from '../types/healthTypes';

// Icon mapping for health log types
const LOG_TYPE_ICONS: Record<string, React.ElementType> = {
  seizure: Zap,
  medication: Pill,
  symptom: Thermometer,
  sleep: Moon,
  feeding: UtensilsCrossed,
  therapy: Activity,
  vital: Heart,
  mood: Smile,
};

export default function HealthLogsPage() {
  const navigate = useNavigate();
  const { childId: paramChildId } = useParams<{ childId: string }>();
  const { familyId, isLoading: isFamilyLoading, hasFamily } = useFamilyContext();

  // Local state
  const [selectedChildId, setSelectedChildId] = useState<string | null>(paramChildId || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showChildSelector, setShowChildSelector] = useState(false);
  const [typeFilter, setTypeFilter] = useState<HealthLogTypeSlug | null>(null);
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({});

  // Queries
  const { data: childrenData, isLoading: isLoadingChildren } = useChildren(familyId || '');

  const {
    data: logsData,
    isLoading: isLoadingLogs,
    error: logsError,
    refetch: refetchLogs,
  } = useHealthLogs(selectedChildId || '', {
    logTypeIds: typeFilter ? [typeFilter] : undefined,
    startDate: dateRange.start,
    endDate: dateRange.end,
    searchQuery: searchQuery || undefined,
  });

  const { data: stats } = useHealthLogStats(selectedChildId || '');

  // Mutations
  const deleteLog = useDeleteHealthLog();

  // Computed
  const children = childrenData?.items ?? [];
  const logs = logsData?.items ?? [];
  const selectedChild = children.find((c) => c.id === selectedChildId);

  // Format date helper
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Handlers
  const handleChildSelect = (childId: string) => {
    setSelectedChildId(childId);
    setShowChildSelector(false);
    // Update URL if we're in a child context
    if (paramChildId) {
      navigate(`/family/children/${childId}/health/logs`);
    }
  };

  const handleLogClick = (log: HealthLog) => {
    if (selectedChildId) {
      navigate(`/family/children/${selectedChildId}/health/logs/${log.id}`);
    }
  };

  const handleDeleteLog = async (log: HealthLog) => {
    if (window.confirm('Delete this health log? This action cannot be undone.')) {
      await deleteLog.mutateAsync({ logId: log.id, childId: log.childId });
    }
  };

  const handleQuickAdd = (type: HealthLogTypeSlug) => {
    if (selectedChildId) {
      navigate(`/family/children/${selectedChildId}/health/logs/new?type=${type}`);
    }
  };

  const clearFilters = () => {
    setTypeFilter(null);
    setDateRange({});
    setSearchQuery('');
  };

  // Loading state
  if (isFamilyLoading || isLoadingChildren) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  // No family state
  if (!hasFamily || !familyId) {
    return <NoFamilyState />;
  }

  // No children state
  if (children.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-8 h-8 text-sage" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-navy mb-3">
            Add a Child First
          </h2>
          <p className="text-warm-gray mb-6">
            To start tracking health logs, you'll need to add a child to your family first.
          </p>
          <Link
            to="/family/children"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Child
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-navy">Health Logs</h1>
          <p className="text-warm-gray mt-1">
            Track symptoms, medications, sleep, and more
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedChildId && (
            <Link
              to={`/family/children/${selectedChildId}/health/logs/new`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Log
            </Link>
          )}
          <Link
            to="/family/health/timeline"
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-warm-gray/20 text-navy rounded-xl hover:bg-cream transition-colors"
          >
            <Calendar className="w-4 h-4" />
            Timeline
          </Link>
        </div>
      </div>

      {/* Child Selector */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative">
            <button
              onClick={() => setShowChildSelector(!showChildSelector)}
              className="flex items-center gap-3 px-4 py-2 bg-cream rounded-lg hover:bg-cream-dark transition-colors min-w-[200px]"
            >
              {selectedChild ? (
                <>
                  <div className="w-8 h-8 bg-sage/20 rounded-full flex items-center justify-center">
                    {selectedChild.avatar_url ? (
                      <img
                        src={selectedChild.avatar_url}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-medium text-sage">
                        {selectedChild.first_name[0]}
                      </span>
                    )}
                  </div>
                  <span className="font-medium text-navy">
                    {selectedChild.first_name} {selectedChild.last_name}
                  </span>
                </>
              ) : (
                <>
                  <User className="w-5 h-5 text-warm-gray" />
                  <span className="text-warm-gray">Select a child</span>
                </>
              )}
              <ChevronDown className="w-4 h-4 text-warm-gray ml-auto" />
            </button>

            {showChildSelector && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowChildSelector(false)}
                />
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-warm-gray/10 shadow-lg z-20 overflow-hidden">
                  {children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleChildSelect(child.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-cream transition-colors ${
                        selectedChildId === child.id ? 'bg-sage/10' : ''
                      }`}
                    >
                      <div className="w-8 h-8 bg-sage/20 rounded-full flex items-center justify-center">
                        {child.avatar_url ? (
                          <img
                            src={child.avatar_url}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium text-sage">
                            {child.first_name[0]}
                          </span>
                        )}
                      </div>
                      <span className="font-medium text-navy">
                        {child.first_name} {child.last_name}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {selectedChildId && (
            <div className="flex items-center gap-2 text-sm text-warm-gray">
              <Link
                to={`/family/children/${selectedChildId}/health`}
                className="hover:text-sage transition-colors"
              >
                Dashboard
              </Link>
              <span>/</span>
              <Link
                to={`/family/children/${selectedChildId}/health/medications`}
                className="hover:text-sage transition-colors"
              >
                Medications
              </Link>
              <span>/</span>
              <Link
                to={`/family/children/${selectedChildId}/health/reports`}
                className="hover:text-sage transition-colors"
              >
                Reports
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Show content only if child is selected */}
      {selectedChildId ? (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-sage" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-navy">{stats.totalLogs}</p>
                    <p className="text-sm text-warm-gray">Total Logs</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-navy">{stats.logsThisPeriod}</p>
                    <p className="text-sm text-warm-gray">This Week</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-terracotta/10 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-terracotta" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-navy">
                      {Object.keys(stats.logsByType).length}
                    </p>
                    <p className="text-sm text-warm-gray">Log Types</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    stats.percentChange > 0
                      ? 'bg-red-100'
                      : stats.percentChange < 0
                      ? 'bg-green-100'
                      : 'bg-gray-100'
                  }`}>
                    {stats.percentChange > 0 ? (
                      <TrendingUp className="w-5 h-5 text-red-600" />
                    ) : stats.percentChange < 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-600 transform rotate-180" />
                    ) : (
                      <Activity className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-navy">
                      {stats.percentChange > 0 ? '+' : ''}{stats.percentChange}%
                    </p>
                    <p className="text-sm text-warm-gray">vs Last Week</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Add */}
          <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
            <h3 className="font-semibold text-navy mb-3">Quick Add</h3>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
              {QUICK_ADD_OPTIONS.map((option) => {
                const Icon = LOG_TYPE_ICONS[option.type] || Activity;
                return (
                  <button
                    key={option.type}
                    onClick={() => handleQuickAdd(option.type)}
                    className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-cream transition-colors group"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                      style={{ backgroundColor: `${option.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: option.color }} />
                    </div>
                    <span className="text-xs text-warm-gray group-hover:text-navy transition-colors">
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-gray hover:text-navy"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg transition-colors ${
                    showFilters ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
                  }`}
                  title="Filters"
                >
                  <Filter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => refetchLogs()}
                  className="p-2 text-warm-gray hover:bg-cream rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-warm-gray/10">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">
                      Log Type
                    </label>
                    <select
                      value={typeFilter || ''}
                      onChange={(e) =>
                        setTypeFilter((e.target.value as HealthLogTypeSlug) || null)
                      }
                      className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                    >
                      <option value="">All Types</option>
                      {QUICK_ADD_OPTIONS.map((option) => (
                        <option key={option.type} value={option.type}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={dateRange.start || ''}
                      onChange={(e) =>
                        setDateRange((prev) => ({ ...prev, start: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={dateRange.end || ''}
                      onChange={(e) =>
                        setDateRange((prev) => ({ ...prev, end: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                    />
                  </div>
                </div>
                {(typeFilter || dateRange.start || dateRange.end) && (
                  <button
                    onClick={clearFilters}
                    className="mt-3 text-sm text-sage hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Logs List */}
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            {isLoadingLogs ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
              </div>
            ) : logsError ? (
              <div className="text-center py-16">
                <p className="text-red-500">Error loading health logs</p>
                <button
                  onClick={() => refetchLogs()}
                  className="mt-2 text-sage hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-16">
                <Activity className="w-12 h-12 text-warm-gray/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-navy mb-2">
                  {searchQuery || typeFilter || dateRange.start
                    ? 'No logs found'
                    : 'No health logs yet'}
                </h3>
                <p className="text-warm-gray mb-4">
                  {searchQuery || typeFilter || dateRange.start
                    ? 'Try adjusting your search or filters'
                    : 'Start tracking health events to build a history'}
                </p>
                {!searchQuery && !typeFilter && !dateRange.start && (
                  <Link
                    to={`/family/children/${selectedChildId}/health/logs/new`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Log
                  </Link>
                )}
              </div>
            ) : (
              <div className="divide-y divide-warm-gray/10">
                {logs.map((log) => {
                  const Icon = LOG_TYPE_ICONS[log.logType.slug] || Activity;
                  return (
                    <button
                      key={log.id}
                      onClick={() => handleLogClick(log)}
                      className="w-full flex items-start gap-4 p-4 hover:bg-cream/50 transition-colors text-left"
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${log.logType.color}20` }}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: log.logType.color }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-navy">
                            {log.logType.name}
                          </span>
                          {log.severity && log.severity > 3 && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                              Severity: {log.severity}/5
                            </span>
                          )}
                        </div>
                        {log.notes && (
                          <p className="text-sm text-warm-gray line-clamp-2 mb-1">
                            {log.notes}
                          </p>
                        )}
                        <div className="flex items-center gap-3 text-xs text-warm-gray">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(log.occurredAt)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(log.occurredAt)}
                          </span>
                          {log.durationMinutes && (
                            <span>{log.durationMinutes} min</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        /* No child selected */
        <div className="bg-white rounded-xl border border-warm-gray/10 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-warm-gray/50 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy mb-2">Select a Child</h3>
          <p className="text-warm-gray">
            Choose a child from the dropdown above to view and manage their health logs.
          </p>
        </div>
      )}
    </div>
  );
}
